import { computed } from 'vue';
import { IPresentableEntity, IEntitiesAnalysisCluster } from '@/models/available-entities.interface';
import store from '@/store';

class EntityAttachingPresenter {

	private availableEntities = computed((): IPresentableEntity[] => {
		return store.getters.availableEntities();
	});

	private filteredEntities = computed((): IPresentableEntity[] => {
		return store.getters.filteredEntities();
	});

	public handleShiftClick(clickedEntity: IPresentableEntity, lastClickedEntityId: string | number | null, attached: boolean): void {
		let indexes = this.getMinAndMaxIndexesOfSelection(clickedEntity, lastClickedEntityId);
		indexes = this.updateMinAndMaxIndexesWithHiddenChildrenInMind(indexes);
		if (!indexes.length) {
			return;
		}

		for (let i = indexes[0]; i <= indexes[1]; i++) {
			// If an entity is in the area of selection, but filtered out, skip it.
			const relevantFilteredEntity = this.filteredEntities.value.find(entity => entity.id === this.availableEntities.value[i].id);
			if (!relevantFilteredEntity) {
				continue;
			}

			store.mutations.setAttachedStatusOfEntity(
				this.availableEntities.value[i].id,
				attached
			);
		}
	}

	private getMinAndMaxIndexesOfSelection(clickedEntity: IPresentableEntity, lastClickedEntityId: string | number | null): number[] {
		const indexOfPreviouslyClickedEntity = this.availableEntities.value.findIndex(
			(entity: IPresentableEntity) => entity.id === lastClickedEntityId
		);
		const indexOfNewlyClickedEntity = this.availableEntities.value.findIndex(
			(entity: IPresentableEntity) => entity.id === clickedEntity.id
		);
		if (indexOfPreviouslyClickedEntity === -1 || indexOfNewlyClickedEntity === -1) {
			return [];
		}
		return [
			Math.min(indexOfPreviouslyClickedEntity, indexOfNewlyClickedEntity),
			Math.max(indexOfPreviouslyClickedEntity, indexOfNewlyClickedEntity)
		];
	}

	private updateMinAndMaxIndexesWithHiddenChildrenInMind(indexes: number[]): number[] {
		if (!indexes.length) {
			return [];
		}

		let maxIndex = indexes[1];
		if (this.availableEntities.value[maxIndex].hideChildren) {
			// Set the cluster the first time, with the id of the parent whose children are hidden as a starting point.
			let entitiesAnalysisCluster = this.iterateOnEntitiesAnalysisCluster({
				relevantEntityIndexes: [this.availableEntities.value[maxIndex].id],
				newestEntityIndexes: []
			});
			// Keep looking for new children until no new ones can be found anymore.
			while (entitiesAnalysisCluster.newestEntityIndexes.length) {
				entitiesAnalysisCluster = this.iterateOnEntitiesAnalysisCluster(entitiesAnalysisCluster);
			}

			// From all the entities that were found, pick the one with the highest index.
			maxIndex = this.availableEntities.value.findLastIndex(
				(entity: IPresentableEntity) => entitiesAnalysisCluster.relevantEntityIndexes.includes(entity.id)
			);
		}

		return [indexes[0], maxIndex];
	}

	private iterateOnEntitiesAnalysisCluster(cluster: IEntitiesAnalysisCluster): IEntitiesAnalysisCluster {
		// For all relevant entity indexes, find any children that are not already in the cluster.
		cluster.newestEntityIndexes = this.availableEntities.value
			.filter((entity: IPresentableEntity) => {
				return entity.parent
					&& cluster.relevantEntityIndexes.includes(entity.parent)
					&& !cluster.relevantEntityIndexes.includes(entity.id);
			})
			.map((entity: IPresentableEntity) => entity.id);

		// Update the relevant entity indexes so that the same children won't be found in the next iteration.
		cluster.relevantEntityIndexes = cluster.relevantEntityIndexes.concat(cluster.newestEntityIndexes);
		return cluster;
	}
}

export default new EntityAttachingPresenter();
